import { useRef, useState, useEffect, useMemo, type ReactNode } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useSwitchActiveWalletChain
} from "thirdweb/react";
import {
  createThirdwebClient,
  getContract,
  prepareContractCall,
  sendTransaction
} from "thirdweb";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
import { useHttp } from "../hooks/useHttp";
import { logger } from "../utils/logger";
import { NS_CHAIN_ID, RIPNDIP_CHAIN, RIPNDIP_CHAIN_ID } from "../config/chain";
import {
  getRipndipImageUrl,
  getRipndipVariantName,
  RIPNDIP_TYPE_TO_RARITY
} from "../utils/ripndip";

const carouselCards = [
  {
    id: 1,
    image: "/images/ripndip/step1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the RIPNDIP tag. A page will open, click <span class='font-medium text-black'>Claim now</span> or <span class='font-medium text-black'>Sign Up</span>."
  },
  {
    id: 2,
    image: "/images/ripndip/step2.png",
    title: "Connect & confirm",
    description:
      "Choose your wallet app and connect it. When asked, <span class='font-medium text-black'>approve the connection</span>."
  },
  {
    id: 3,
    image: "/images/ripndip/step3.png",
    title: "Claim your digital collectible",
    description:
      "The collectible preview will appear. Tap Claim and your RIPNDIP collectible is now yours!"
  }
];

type DrawerType =
  | "loading"
  | "wallet_connect"
  | "scan_chip"
  | "claim_nft"
  | "claiming"
  | "success"
  | "already_claimed"
  | "error"
  | null;

interface RipndipClaimResponse {
  success: boolean;
  action: "claim" | "already_claimed" | "wallet_not_connected";
  uid?: string;
  timestamp?: number;
  tokenType?: number;
  signature?: string;
  message: string;
}

export const ClaimRipndip = () => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const address = account?.address;
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      account
    );
  }, [wallet, account]);
  const eoaAddress = eoaAccount?.address;
  const activeWalletChain = useActiveWalletChain();
  const switchActiveWalletChain = useSwitchActiveWalletChain();

  const uid = searchParams.get("uid");

  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);
  const [claimResponse, setClaimResponse] =
    useState<RipndipClaimResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  const contractAddress = process.env.REACT_APP_RIPNDIP_CONTRACT_ADDRESS;
  const requiredChain = RIPNDIP_CHAIN;
  const activeTokenType = claimResponse?.tokenType;
  const previewImage = getRipndipImageUrl(activeTokenType);

  const settings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => setCurrentSlide(current)
  };

  useEffect(() => {
    let isMounted = true;

    const validateAndCallAPI = async () => {
      if (!uid) {
        logger.ui("Ripndip claim page loaded without UID parameter");
        return;
      }

      if (!/^[A-F0-9]+$/i.test(uid)) {
        if (isMounted) {
          setError("Invalid UID format");
          setCurrentDrawer("error");
        }
        return;
      }

      if (!address) {
        if (isMounted) setCurrentDrawer("wallet_connect");
        return;
      }

      if (!isMounted) return;
      setCurrentDrawer("loading");
      setError(null);

      try {
        const addressForSigning = eoaAddress || address;
        const { data } = await sendRequest<RipndipClaimResponse>({
          endpoint: `/api/ripndip_claim?uid=${encodeURIComponent(
            uid
          )}${addressForSigning ? `&address=${encodeURIComponent(addressForSigning)}` : ""}`,
          method: "GET",
          responseType: "json"
        });

        if (!isMounted) return;

        setClaimResponse(data);

        if (!data.success) {
          setError(data.message);
          setCurrentDrawer(
            data.action === "already_claimed" ? "already_claimed" : "error"
          );
          return;
        }

        switch (data.action) {
          case "claim":
            setCurrentDrawer("claim_nft");
            break;
          case "wallet_not_connected":
            setCurrentDrawer("wallet_connect");
            break;
          case "already_claimed":
            setError(
              data.message || "This RIPNDIP NFT has already been claimed"
            );
            setCurrentDrawer("already_claimed");
            break;
          default:
            setError("Unknown response from server");
            setCurrentDrawer("error");
        }
      } catch (err: any) {
        if (!isMounted) return;
        if (err?.name === "AbortError" || err?.message?.includes("abort")) {
          logger.ui(
            "Request aborted (component unmounted or new request started)"
          );
          return;
        }
        logger.apiError("Ripndip claim API call error", err as Error);
        setError(err instanceof Error ? err.message : "Failed to validate UID");
        setCurrentDrawer("error");
      }
    };

    validateAndCallAPI();

    return () => {
      isMounted = false;
    };
  }, [uid, address, eoaAddress, sendRequest]);

  useEffect(() => {
    if (!uid && address && currentDrawer === "wallet_connect") {
      setCurrentDrawer("scan_chip");
    }
  }, [uid, address, currentDrawer]);

  useEffect(() => {
    document.body.style.overflow = currentDrawer ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  useEffect(() => {
    const autoSwitchToBase = async () => {
      if (!uid) {
        return;
      }

      if (
        address &&
        activeWalletChain &&
        activeWalletChain.id !== RIPNDIP_CHAIN_ID &&
        activeWalletChain.id !== NS_CHAIN_ID &&
        switchActiveWalletChain &&
        !isSwitchingChain
      ) {
        try {
          setIsSwitchingChain(true);
          await switchActiveWalletChain(requiredChain);
        } catch (err: any) {
          logger.walletError("Auto-switch to Base failed", err as Error);
        } finally {
          setIsSwitchingChain(false);
        }
      }
    };

    const timeoutId = setTimeout(autoSwitchToBase, 100);
    return () => clearTimeout(timeoutId);
  }, [
    uid,
    address,
    activeWalletChain?.id,
    switchActiveWalletChain,
    requiredChain,
    isSwitchingChain
  ]);

  const handleSwitchNetwork = async () => {
    if (!switchActiveWalletChain) return;
    setIsSwitchingChain(true);
    setError(null);

    try {
      await switchActiveWalletChain(requiredChain);
    } catch (err: any) {
      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected")
      ) {
        setError(
          "Network switch cancelled. Please switch to Base to continue."
        );
      } else {
        setError(
          "Failed to switch network. Please switch manually in your wallet."
        );
      }
    } finally {
      setIsSwitchingChain(false);
    }
  };

  const handleClaimEntry = () => {
    setError(null);
    setCurrentDrawer(address ? "scan_chip" : "wallet_connect");
  };

  const handleClaimNFT = async () => {
    if (
      !address ||
      !account ||
      !claimResponse ||
      claimResponse.action !== "claim"
    ) {
      return;
    }

    const { uid: claimUid, timestamp, signature } = claimResponse;

    if (!claimUid || signature == null || timestamp == null) {
      setError("Missing claim parameters");
      return;
    }

    if ((activeWalletChain?.id || 0) !== RIPNDIP_CHAIN.id) {
      setError("Please switch to the target network");
      return;
    }

    setIsClaiming(true);
    setCurrentDrawer("claiming");
    setError(null);

    try {
      const response = await fetch("/RipndipAbi.json");
      const abi = await response.json();

      if (!contractAddress) {
        throw new Error("Contract address not configured");
      }

      const client = createThirdwebClient({
        clientId: process.env.REACT_APP_THIRDWEB_CLIENT_ID as string
      });

      const contract = getContract({
        client,
        chain: requiredChain,
        address: contractAddress,
        abi
      });

      const caller = eoaAddress || address;
      const transaction = prepareContractCall({
        contract,
        method: "claim",
        params: [timestamp, caller, claimUid, signature]
      });

      const receipt = await sendTransaction({
        account,
        transaction
      });

      const nextHash = (receipt as any)?.transactionHash;
      setTxHash(nextHash || null);

      if (nextHash) {
        setCurrentDrawer("success");
      } else {
        throw new Error("Transaction failed");
      }
    } catch (err: any) {
      logger.contractError("Failed to claim Ripndip NFT", err as Error, {
        uid: claimResponse?.uid,
        address,
        contractAddress
      });

      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected")
      ) {
        setError("Transaction cancelled. Please try again.");
        setCurrentDrawer("claim_nft");
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to claim RIPNDIP NFT"
        );
        setCurrentDrawer("error");
      }
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div className="py-5">
      <div className="px-6 mb-8">
        {!uid && (
          <div className="rounded-[22px] overflow-hidden relative aspect-square">
            <img
              src="/images/ripndip/ripndip-landing.png"
              alt="223 x Ripndip x Ponke"
              className="object-cover absolute inset-0 w-full h-full"
            />
            <div className="absolute inset-x-4 bottom-4 p-4 text-center">
              <button
                onClick={handleClaimEntry}
                className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
              >
                Claim Now
              </button>
            </div>
          </div>
        )}

        {/* {uid && (
          <div className="rounded-[22px] overflow-hidden aspect-square relative mb-4 bg-[#F6F0D8]">
            <img
              src={previewImage}
              alt="Ripndip preview"
              className="object-cover absolute inset-0 w-full h-full"
            />
          </div>
        )} */}


          {uid && (
          <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mb-4">
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 animate-pulse" />
            <img
              src="/images/ripndip/ripndip-landing.png"
              alt="Ripndip"
              className="object-cover absolute inset-0 w-full h-full"
            />
          </div>
        )}
      </div>

      <div className="px-6">
        <h3 className="mb-4 text-2xl font-bold text-center">How it Works</h3>

        <div className="relative carousel-container">
          <Slider ref={sliderRef} {...settings}>
            {carouselCards.map((card) => (
              <div
                key={card.id}
                className="w-full rounded-[14px] overflow-hidden bg-[#F7F0D4] shadow-xl mx-auto"
              >
                <div className="relative w-full h-full rounded-[14px] overflow-hidden aspect-[3/4]">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="object-cover absolute inset-0 w-full h-full"
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        <div className="my-3 text-center">
          <h4 className="mb-2 text-xl font-bold">
            {carouselCards[currentSlide].title}
          </h4>
          <p
            className="text-lg leading-tight text-[#626262] whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: carouselCards[currentSlide].description
            }}
          />
        </div>

        <div className="flex gap-2 justify-center items-center mt-5 mb-6">
          {carouselCards.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${
                currentSlide === index
                  ? "w-[37px] h-[10px] bg-[#000000]"
                  : "w-[10px] h-[10px] bg-[#CCCCCC]"
              }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {currentDrawer === "loading" && (
        <DrawerShell>
          <div className="flex justify-center items-center w-16 h-16 bg-[#F6F0D8] rounded-full animate-spin">
            <svg
              className="w-8 h-8 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <div className="text-center space-y-2.5 mt-8">
            <div className="text-2xl font-bold text-black">Validating UID</div>
            <div className="text-[#626262]">
              Please wait while we verify your RIPNDIP collectible...
            </div>
          </div>
        </DrawerShell>
      )}

      {currentDrawer === "wallet_connect" && (
        <DrawerShell>
          <div className="mb-4">
            <img src="/svg/223-logo.svg" alt="223 Logo" />
          </div>
          <div className="text-center space-y-2.5 w-full">
            <div className="text-2xl font-bold text-black">Sign In</div>
            <div className="text-[#626262]">
              To claim your 223 Digital Collectible, sign in or create an
              account
            </div>
            <div className="!mt-6 flex justify-center">
              <ThirdwebWalletConnect showDropdown={false} />
            </div>
          </div>
        </DrawerShell>
      )}

      {currentDrawer === "scan_chip" && (
        <DrawerShell>
          <div className="w-full">
            <img
              src="/images/ripndip/wallet-connected.png"
              alt="Ripndip wallet connected"
              className="rounded-[22px] w-full"
            />
            <div className="mt-5 text-center">
              <div className="text-[20px] font-bold leading-tight text-black">
                Connected Successfully!
              </div>
              <div className="text-[#626262] mt-2 leading-tight">
                Please close the browser tab and scan the NFC chip again within
                5 minutes to complete the claim.
              </div>
            </div>
            <button
              onClick={() => setCurrentDrawer(null)}
              className="py-2 w-full font-bold text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 mt-5"
            >
              Close and tap again
            </button>
          </div>
        </DrawerShell>
      )}

      {currentDrawer === "claim_nft" && claimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="space-y-2.5">
              <div className="rounded-[22px] overflow-hidden aspect-square relative mx-auto bg-[#F7F0D4]">
                <img
                  src={previewImage}
                  alt="Ripndip"
                  className="object-cover absolute inset-0 w-full h-full"
                />
                <div className="backdrop-blur-md rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/30">
                  <div className="max-w-[260px] mx-auto flex flex-col items-center justify-center gap-1">
                    <h3 className="text-xl font-bold text-center">
                      RIPNDIP x Ponke <br />
                      Authenticity Certificate
                      {/* {getRipndipVariantName(activeTokenType)} */}
                    </h3>
                    {(activeWalletChain?.id || 0) === RIPNDIP_CHAIN_ID && (
                      <button
                        onClick={handleClaimNFT}
                        disabled={isClaiming}
                        className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
                      >
                        Claim Now
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              {(activeWalletChain?.id || 0) !== RIPNDIP_CHAIN_ID && (
                <div>
                  <div className="px-4 py-3 mb-2 text-sm text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
                    Please switch to Base to continue
                  </div>
                  <button
                    onClick={handleSwitchNetwork}
                    disabled={isSwitchingChain}
                    className="py-2 w-full text-lg font-bold text-white bg-black disabled:opacity-50"
                  >
                    {isSwitchingChain ? "Switching..." : "Switch Network"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "claiming" && (
        <DrawerShell>
          <img src="/images/ripndip/claim-now.png" alt="claiming" className="rounded-[22px]" />
          <div className="text-center space-y-2.5 mt-8">
            <div className="text-2xl font-bold text-black">Claiming</div>
            <div className="text-[#626262]">
              Claiming your Digital Collectible. Please wait.
            </div>
            {txHash && (
              <div className="px-4 text-xs text-gray-500 break-all">
                Transaction:{" "}
                <a
                  href={`https://basescan.org/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {txHash.slice(0, 10)}...{txHash.slice(-8)}
                </a>
              </div>
            )}
          </div>
        </DrawerShell>
      )}

      {currentDrawer === "success" && claimResponse && (
        <DrawerShell>
          <div className="space-y-2 text-center">
            <div className="text-lg font-bold text-center">🎉 CONGRATS!</div>
            <div className="text-[#626262]">
              Congratulations! Your RIPNDIP x Ponke Digital Collectible has been claimed.
            </div>
            {txHash && (
              <div className="px-4 text-xs text-gray-500 break-all">
                Transaction:{" "}
                <a
                  href={`https://basescan.org/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {txHash.slice(0, 10)}...{txHash.slice(-8)}
                </a>
              </div>
            )}
            <div className="rounded-[22px] overflow-hidden aspect-square relative mx-auto bg-[#F7F0D4]">
              <img
                src={previewImage}
                alt="Ripndip"
                className="object-cover absolute inset-0 w-full h-full"
              />
            </div>
            <h3 className="text-xl font-bold text-center">
                      {getRipndipVariantName(activeTokenType)}
                    </h3>
            <div className="text-[#626262]">Certificate of Authenticity</div>
            <button
              onClick={() => navigate("/rewards/ripndip")}
              className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] hover:bg-gray-800 !mt-6"
            >
              Go to profile
            </button>
          </div>
        </DrawerShell>
      )}

      {currentDrawer === "already_claimed" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit text-center">
            <img
              src="/images/ripndip/wallet-connected.png"
              alt="wallet-connected"
              className="mx-auto mb-4 rounded-[22px]"
            />
            <h2 className="mb-2 text-xl font-bold">Already Claimed</h2>
            <p className="mb-6 text-gray-600">
              {"This RIPNDIP NFT has already been claimed"}
            </p>
            <button
              onClick={() => navigate("/rewards/ripndip")}
              className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] hover:bg-gray-800"
            >
              Go to profile
            </button>
          </div>
        </div>
      )}

      {currentDrawer === "error" && (
        <DrawerShell>
          <img
            src="/images/ripndip/error.png"
            alt="error"
            className="mb-4 rounded-[22px]"
          />
          <div className="text-center space-y-2.5">
            <div className="text-2xl font-bold text-black">
              Something Went Wrong
            </div>
            <div className="text-[#626262]">
              {error || "An unexpected error occurred. Please try again."}
            </div>
            <button
              onClick={() =>
                window.open("/support", "_blank", "noopener,noreferrer")
              }
              className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] hover:bg-gray-800 !mt-6"
            >
              Contact Support
            </button>
          </div>
        </DrawerShell>
      )}
    </div>
  );
};

const DrawerShell = ({ children }: { children: ReactNode }) => (
  <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
    <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
      {children}
    </div>
  </div>
);
